/**
 * Chip layer — replaces packages/amgdragontiger_v2/lib/presentation/flame/dragontiger_flame_game.dart.
 *
 * Coordinate system: 1792×828 reference (PixiLayer's logical size).
 *
 * Bet-area scatter rectangles (Flame `ChipPosition(minX%, rangeXpx, minY%, rangeYpx)`,
 * dragontiger_flame_game.dart:32-36):
 *   Dragon (262): (0.22, 200, 0.41, 200)  →  top-left (394, 339), scatter 200×200
 *   Tiger  (263): (0.67, 200, 0.41, 200)  →  top-left (1201, 339), scatter 200×200
 *   Tie    (264): (0.45, 180, 0.55, 160)  →  top-left (806, 455), scatter 180×160
 *
 * Player origin (chips fly FROM): (size.x/2, size.y + 50) = (896, 878).
 * Dealer target   (chips fly TO on lose): (size.x*0.5, size.y*0.2) = (896, 166).
 * Player target   (chips fly TO on win):  (size.x*0.5, size.y*1.1) = (896, 911).
 *
 * Animations:
 *   Placement: 0.3s linear (Flame EffectController default).
 *   Win/Lose:  0.5s with 0.02s × chipIndex stagger, opacity fades to 0.2.
 *   Tie:       half the chips → dealer, the other half → player.
 *   After 600 ms post-trigger the chips are destroyed (Flame Future.delayed).
 */
import { Container } from 'pixi.js';
import type { BetType } from '../types/game';
import { ChipSprite, preloadCoinTextures } from './chipSprite';
import { Easings, tween } from './tween';
import { REF_HEIGHT, REF_WIDTH } from '../config';

interface XY {
  x: number;
  y: number;
}

interface ScatterRect {
  minX: number; // top-left x (px)
  rangeX: number; // scatter range (px)
  minY: number;
  rangeY: number;
}

const SCATTER: Record<BetType, ScatterRect> = {
  dragon: {
    minX: 0.22 * REF_WIDTH,
    rangeX: 200,
    minY: 0.41 * REF_HEIGHT,
    rangeY: 200,
  },
  tiger: {
    minX: 0.67 * REF_WIDTH,
    rangeX: 200,
    minY: 0.41 * REF_HEIGHT,
    rangeY: 200,
  },
  tie: {
    minX: 0.45 * REF_WIDTH,
    rangeX: 180,
    minY: 0.55 * REF_HEIGHT,
    rangeY: 160,
  },
};

const PLAYER_ORIGIN: XY = { x: REF_WIDTH / 2, y: REF_HEIGHT + 50 };
const DEALER_TARGET: XY = { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 0.2 };
const PLAYER_WIN_TARGET: XY = { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 1.1 };

interface ActiveChip {
  bet: BetType;
  sprite: ChipSprite;
}

export class ChipLayer {
  private readonly root: Container;
  private active: ActiveChip[] = [];

  constructor(parent: Container) {
    this.root = new Container();
    this.root.label = 'ChipLayer';
    parent.addChild(this.root);
    // Warm CDN textures so the first chip doesn't blink the fallback.
    void preloadCoinTextures();
  }

  /**
   * Add a chip at a random scatter point inside the bet area.
   * `chipIndex` is the index into the GameConfig.chipValues array — this is
   * what selects the coin image (imgCoins{(index%6)+1}.png), matching the
   * Flutter Flame port (`addChip(betId, chipIndex)` in
   * dragontiger_flame_game.dart:155-176). Using the selected chip's index
   * ensures the flying chip's colour matches the picker's selected chip.
   */
  placeChip(bet: BetType, chipValue: number, chipIndex: number): void {
    const rect = SCATTER[bet];
    const target: XY = {
      x: rect.minX + Math.random() * rect.rangeX,
      y: rect.minY + Math.random() * rect.rangeY,
    };

    const sprite = new ChipSprite(chipValue, chipIndex);
    sprite.position.set(PLAYER_ORIGIN.x, PLAYER_ORIGIN.y);
    this.root.addChild(sprite);

    const from = { ...PLAYER_ORIGIN };
    tween({
      durationMs: 300, // Flame EffectController(duration: 0.3)
      easing: Easings.linear,
      onUpdate: (t) => {
        sprite.position.set(
          from.x + (target.x - from.x) * t,
          from.y + (target.y - from.y) * t,
        );
      },
    });

    this.active.push({ bet, sprite });
  }

  /** Animate all chips to the player target (win). Auto-destroys after 600 ms. */
  animateWinningChips(winningBets: ReadonlySet<BetType>): void {
    // Player-wins-everything path: any non-zero winningBets means we won at
    // least one area. We still fly losing-area chips to the dealer so the
    // visual matches reality (Flame's moveAllToPlayer flies ALL chips, but
    // the orchestrator only calls this when there was a positive WinLose —
    // here we split by bet to be more accurate).
    const oldActive = this.active;
    this.active = [];
    oldActive.forEach((c, i) => {
      const target = winningBets.has(c.bet) ? PLAYER_WIN_TARGET : DEALER_TARGET;
      this.flyAndFade(c, target, i);
    });
    this.scheduleDestroyAll(oldActive, 600);
  }

  /** Animate all chips to the dealer target (lose). */
  animateLosingChips(): void {
    const oldActive = this.active;
    this.active = [];
    oldActive.forEach((c, i) => this.flyAndFade(c, DEALER_TARGET, i));
    this.scheduleDestroyAll(oldActive, 600);
  }

  /**
   * Tie animation. Matches Flame moveHalfHalf
   * (dragontiger_flame_game.dart:198-210): first half of chips → dealer,
   * second half → player. Insertion order = chronological placement.
   */
  animateTie(): void {
    const oldActive = this.active;
    this.active = [];
    const half = Math.ceil(oldActive.length / 2);
    oldActive.forEach((c, i) => {
      const target = i < half ? DEALER_TARGET : PLAYER_WIN_TARGET;
      this.flyAndFade(c, target, i);
    });
    this.scheduleDestroyAll(oldActive, 600);
  }

  /** Wipe all chips immediately (no animation). Used on Clear / new round. */
  hardClear(): void {
    for (const c of this.active) c.sprite.destroy();
    this.active = [];
  }

  hasAnyChips(): boolean {
    return this.active.length > 0;
  }

  // -----------------------------------------------------------------

  /**
   * 0.5 s tween to target, opacity drops to 0.2, with a stagger of
   * 0.02 s × index (matches ChipComponent.animateTo).
   */
  private flyAndFade(c: ActiveChip, target: XY, index: number): void {
    const fromX = c.sprite.position.x;
    const fromY = c.sprite.position.y;
    const fromAlpha = c.sprite.alpha;
    const delayMs = 20 * index;

    window.setTimeout(() => {
      tween({
        durationMs: 500,
        easing: Easings.linear,
        onUpdate: (t) => {
          c.sprite.position.set(
            fromX + (target.x - fromX) * t,
            fromY + (target.y - fromY) * t,
          );
          c.sprite.alpha = fromAlpha + (0.2 - fromAlpha) * t;
        },
      });
    }, delayMs);
  }

  private scheduleDestroyAll(list: ActiveChip[], delayMs: number): void {
    window.setTimeout(() => {
      for (const c of list) {
        try {
          c.sprite.destroy();
        } catch {
          /* already destroyed */
        }
      }
    }, delayMs);
  }
}
